import { useState, useCallback, useEffect } from 'react';
import { useCapacitor } from './useCapacitor';

// Types pour l'authentification biométrique
interface BiometricType {
  isAvailable: boolean;
  biometryType?: 'touchId' | 'faceId' | 'fingerprint' | 'unknown';
  strongBiometryIsAvailable: boolean;
}

export const useBiometric = () => {
  const { isNative } = useCapacitor();
  const [isAvailable, setIsAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  // Check availability on mount
  useEffect(() => {
    checkAvailability();
  }, [isNative]);

  const checkAvailability = useCallback(async () => {
    if (!isNative) {
      setIsAvailable(false);
      setIsChecking(false);
      return false;
    }

    try {
      // Import dynamique pour éviter les erreurs sur web
      const { NativeBiometric } = await import('capacitor-native-biometric');
      
      const result = await NativeBiometric.isAvailable();
      setIsAvailable(result.isAvailable);
      setBiometryType(result.biometryType?.toString() || null);
      setIsChecking(false);
      return result.isAvailable;
    } catch (error) {
      console.error('Biometric check error:', error);
      setIsAvailable(false);
      setIsChecking(false);
      return false;
    }
  }, [isNative]);

  const authenticate = useCallback(async (reason: string = 'Authenticate to continue') => {
    if (!isNative || !isAvailable) {
      return { success: false, error: 'Biometric not available' };
    }

    try {
      const { NativeBiometric } = await import('capacitor-native-biometric');
      
      await NativeBiometric.verifyIdentity({
        reason,
        title: 'Authentication Required',
        subtitle: 'Please authenticate to continue',
        description: reason,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Biometric authentication error:', error);
      return { 
        success: false, 
        error: error.message || 'Authentication failed' 
      };
    }
  }, [isNative, isAvailable]);

  const setCredentials = useCallback(async (
    username: string,
    password: string
  ) => {
    if (!isNative || !isAvailable) {
      return false;
    }

    try {
      const { NativeBiometric } = await import('capacitor-native-biometric');
      
      await NativeBiometric.setCredentials({
        username,
        password,
        server: 'nazandoo-marketplace',
      });

      return true;
    } catch (error) {
      console.error('Set credentials error:', error);
      return false;
    }
  }, [isNative, isAvailable]);

  const getCredentials = useCallback(async () => {
    if (!isNative || !isAvailable) {
      return null;
    }

    try {
      const { NativeBiometric } = await import('capacitor-native-biometric');
      
      const credentials = await NativeBiometric.getCredentials({
        server: 'tinda-marketplace',
      });

      return credentials;
    } catch (error) {
      console.error('Get credentials error:', error);
      return null;
    }
  }, [isNative, isAvailable]);

  const deleteCredentials = useCallback(async () => {
    if (!isNative) {
      return false;
    }

    try {
      const { NativeBiometric } = await import('capacitor-native-biometric');
      
      await NativeBiometric.deleteCredentials({
        server: 'tinda-marketplace',
      });

      return true;
    } catch (error) {
      console.error('Delete credentials error:', error);
      return false;
    }
  }, [isNative]);

  return {
    isAvailable,
    biometryType,
    isChecking,
    isSupported: isNative,
    authenticate,
    setCredentials,
    getCredentials,
    deleteCredentials,
    checkAvailability
  };
};
